'use client';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { filterVipFromInsuranceCatalog } from '@/util/vip-insurance';

import './SignupForm.style.scss';

import { CACHE_TIME } from '@/const/cache';
import { SIGNUP_ROUTES } from '@/const/signup';
import { RawEPCCCartResponse } from '@/types/Cart';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import analytics from '@/util/analytics';
import { sleep } from '@/util/misc';
import { updateMembership } from '@/util/user_client';

import { SignupState as RegistrationValues } from '@/types/Signup';

import { useSignup } from '@/components/hook/signup';
import { useApplicationSubmit } from '@/components/hook/applicationSubmit';

import SignupStep1Form, {
  SignupStep1FormValues
} from '../SignupStep1Form/SignupStep1Form';
import SignupStep2Form, {
  SignupStep2FormValues
} from '../SignupStep2Form/SignupStep2Form';
import SignupStep3Form, {
  SignupStep3FormValues
} from '../SignupStep3Form/SignupStep3Form';
import SignupStep4Form, {
  SignupStep4FormValues
} from '../SignupStep4Form/SignupStep4Form';
import SignupStep5Form, {
  SignupStep5FormValues
} from '../SignupStep5Form/SignupStep5Form';
import { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import { useSignupAnalytics } from '@/components/hook/analytics';
import { APPLICATION_FLOW, ApplicationFlow } from '@/const/application_flow';

type SignupFormProps = {
  currentStep: number;
  /**
   * Which signup experience the visitor came through. This component renders
   * `/apply/review` for BOTH flows, so the conversion events below fire from
   * both — without this they cannot be attributed to a flow.
   */
  applicationFlow?: ApplicationFlow;
  subscriptionData: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  planSelected: any;
  referralCopy?: ReferralCopyProps;
  user: RegistrationValues;
  previewCart?: RawEPCCCartResponse;
  stripeCouponId?: string;
  requiresAuthorizationCharge?: boolean;
};

/**
 *  See developerCookieJar.md for why this is a temporary but currently necessary evil,
 *  and should be done away with because a) ugly and b) bad
 **/
const deconstructInitialFormValues = (
  user: RegistrationValues
): {
  step1: SignupStep1FormValues;
  step2: SignupStep2FormValues;
  step3: SignupStep3FormValues;
  step4: SignupStep4FormValues;
  step5: SignupStep5FormValues;
} => {
  const step1InitialValues: SignupStep1FormValues = (({
    email,
    phone,
    password,
    referralCode,
    optinPromotion,
    optinText
  }) => ({ email, phone, password, referralCode, optinPromotion, optinText }))(
    user
  );

  const step2InitialValues: SignupStep2FormValues = (({
    firstName,
    lastName,
    birthday,
    instagram,
    tiktok
  }) => ({ firstName, lastName, birthday, instagram, tiktok }))(user);

  const step3InitialValues: SignupStep3FormValues = (({
    address,
    aptUnitSuite,
    city,
    state,
    postalCode
  }) => ({ address, aptUnitSuite, city, state, postalCode }))(user);

  const step4InitialValues: SignupStep4FormValues = (({
    creditCardLast4,
    expirationDate,
    sameAsShippingAddress,
    stripePaymentMethodTokenId,
    stripePaymentMethodBrand,
    billingAddress,
    billingAptUnitSuite,
    billingCity,
    billingState,
    billingPostalCode
  }) => ({
    creditCardLast4,
    expirationDate,
    sameAsShippingAddress,
    stripePaymentMethodTokenId,
    stripePaymentMethodBrand,
    billingAddress,
    billingAptUnitSuite,
    billingCity,
    billingState,
    billingPostalCode
  }))(user);

  const step5InitialValues: SignupStep5FormValues = (({
    paymentAgreement,
    terms
  }) => ({ paymentAgreement, terms }))(user);

  return {
    step1: step1InitialValues,
    step2: step2InitialValues,
    step3: step3InitialValues,
    step4: step4InitialValues,
    step5: step5InitialValues
  };
};

export default function SignupForm(props: SignupFormProps) {
  const {
    currentStep,
    applicationFlow = APPLICATION_FLOW.MULTI_PAGE,
    user,
    subscriptionData,
    insuranceData,
    previewCart,
    referralCopy,
    stripeCouponId,
    requiresAuthorizationCharge
  } = props;

  const eligibleInsuranceData = useMemo(
    () => filterVipFromInsuranceCatalog(insuranceData),
    [insuranceData]
  );

  const router = useRouter();

  // Still needed by the per-step handlers below, which persist each step's
  // values and fire its funnel event before routing on.
  const { actions: signupActions } = useSignup();
  const { metadata: userSignupAnalytics } = useSignupAnalytics(user);

  /**
   * The submission pipeline now lives in `useApplicationSubmit` so the
   * single-page application can run the same one. This flow tokenizes a card
   * on step 4, so the customer does not exist yet — hence the default
   * `card-token` payment source.
   */
  const {
    submitApplication,
    errorMessage,
    setErrorMessage,
    isSubmitting: submissionIsLoading
  } = useApplicationSubmit({
    applicationFlow,
    user,
    subscriptionData,
    insuranceData,
    previewCart,
    stripeCouponId,
    requiresAuthorizationCharge
  });

  const initialValues = deconstructInitialFormValues(user);

  /**
   * Creates an assoicated user in Front
   * @param user
   */
  const createFrontUser = async (user: RegistrationValues) => {
    try {
      const response = await fetch('/api/front/create-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create Front user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating Front user:', error);
      // Non-blocking error - we don't want to fail signup if Front integration fails
    }
  };

  /**
   * Applies desired membership plans to user
   * @param user
   */
  const setMembership = async (user: RegistrationValues) => {
    // Compile membership selections
    const basePlanId = user.plan?.stripePlanId;
    const insurancePlanId = eligibleInsuranceData.find(
      (plan) => plan.slug === user.insurance_level
    )?.stripePlanId;

    if (!basePlanId || !insurancePlanId) {
      // With form validation we should never get here, but if we do..setErrorMessage
      throw Error('Subscription and/or insurance plans not selected');
    }

    const subscriptionPlanIds = [basePlanId];

    const addonPlanId = subscriptionData.find(
      (plan) => plan.slug === user.addon_subscription
    )?.stripePlanId;

    if (addonPlanId) {
      subscriptionPlanIds.push(addonPlanId);
    }

    // Save user's subscription selections
    await updateMembership(subscriptionPlanIds, insurancePlanId);
  };

  if (!!errorMessage && currentStep !== 4) {
    setErrorMessage(null);
  }

  return (
    <div className='SignupForm'>
      {currentStep === 0 && (
        <SignupStep1Form
          key={1}
          initialValues={initialValues.step1}
          onSubmitSuccess={(values) => {
            const payload = JSON.stringify({
              email: values.email,
              optinPromotion: values.optinPromotion
                ? 'opted_in'
                : 'unsubscribed',
              phone: values.phone,
              optinText: values.optinText,
              source: 'application_step_1'
            });
            const requestOptions = {
              method: 'POST',
              body: payload,
              next: { revalidate: CACHE_TIME.NO_CACHE }
            };

            // THIS IS A HACK.
            // I don't know why, but doing just 1 API all to Braze doesn't work.
            // Space out the calls between 10 seconds to get it working as expected
            // A support ticket with Braze has been opened.

            // No need to await the fetch here. Just let it go and die if it fails.
            fetch('/api/marketing/abandon', requestOptions)
              .then(() => {
                return sleep(10000);
              })
              .then(() => {
                fetch('/api/marketing/abandon', requestOptions);
              })
              .catch(() => {
                // TODO: Put error in DataDog/BugSnag whatever
                console.error('Error when adding user to marketing AA list');
              });

            signupActions.updateFormValues(values);
            console.warn('[segment] Application - Start Application Finished', {
              application_flow: applicationFlow
            });
            analytics.track('Application - Start Application Finished', {
              application_flow: applicationFlow
            });
            router.push(SIGNUP_ROUTES.PERSONALIZE);
          }}
          referralCopy={referralCopy}
          user={user}
        />
      )}

      {currentStep === 1 && (
        <SignupStep2Form
          key={2}
          initialValues={initialValues.step2}
          onSubmitSuccess={(values) => {
            signupActions.updateFormValues(values);
            console.warn(
              '[segment] Application - Customer Information Finished',
              {
                application_flow: applicationFlow,
                success: true,
                ...userSignupAnalytics
              }
            );
            analytics.track('Application - Customer Information Finished', {
              application_flow: applicationFlow,
              success: true,
              ...userSignupAnalytics
            });
            router.push(SIGNUP_ROUTES.ADDRESS);
          }}
        />
      )}

      {currentStep === 2 && (
        <SignupStep3Form
          key={3}
          initialValues={initialValues.step3}
          onSubmitSuccess={(values) => {
            signupActions.updateFormValues(values);
            console.warn('[segment] Application - Address Finished', {
              application_flow: applicationFlow,
              success: true,
              ...userSignupAnalytics
            });
            analytics.track('Application - Address Finished', {
              application_flow: applicationFlow,
              success: true,
              ...userSignupAnalytics
            });
            router.push(SIGNUP_ROUTES.BILLING);
          }}
        />
      )}

      {currentStep === 3 && (
        <SignupStep4Form
          key={4}
          initialValues={initialValues.step4}
          onSubmitSuccess={(values) => {
            signupActions.updateFormValues(values);
            console.warn('[segment] Application - Payment Finished', {
              application_flow: applicationFlow,
              success: true,
              ...userSignupAnalytics
            });
            analytics.track('Application - Payment Finished', {
              application_flow: applicationFlow,
              success: true,
              ...userSignupAnalytics
            });
            router.push(SIGNUP_ROUTES.REVIEW);
          }}
        />
      )}

      {currentStep === 4 && (
        <SignupStep5Form
          key={5}
          initialValues={initialValues.step5}
          user={user}
          errorMessage={errorMessage}
          subs={subscriptionData}
          insuranceData={eligibleInsuranceData}
          previewCart={previewCart}
          referralCopy={referralCopy}
          onSubmitSuccess={() => {
            submitApplication(user);
          }}
          submissionIsLoading={submissionIsLoading}
        />
      )}
    </div>
  );
}
